import React, { useEffect, useState } from "react";
import { supabase } from "../supabase/client";

export default function Revenue() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  
  // 1. New State for Search
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchRevenue();
  }, []);

  async function fetchRevenue() {
    const { data, error } = await supabase
      .from("orders")
      .select(`
        id,
        total_amount,
        created_at,
        payment_method,
        trx_id,
        app_users:app_user_id (full_name, phone)
      `)
      .eq("payment_status", "paid")
      .order("created_at", { ascending: false });
  
    if (error) console.error(error);
    setTransactions(data || []);
    const total = data?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
    setTotalRevenue(total);
    setLoading(false);
  }

  // 2. Filter Logic
  const filteredTransactions = transactions.filter((t) => {
    const query = searchQuery.toLowerCase();
    const orderId = t.id?.toLowerCase() || "";
    const customerName = t.customers?.full_name?.toLowerCase() || "";
    
    // Search checks both ID and Name
    return orderId.includes(query) || customerName.includes(query);
  });

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-6xl mx-auto">
        
        {/* --- HEADER & TOTAL CARD --- */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
          <div>
            <h1 className="text-2xl font-black tracking-tight uppercase italic">Revenue_Insight</h1>
            <p className="text-slate-500 text-sm font-medium">Overview of confirmed successful transactions.</p>
          </div>
  
          <div className="bg-slate-900 text-white p-6 rounded-[2.5rem] shadow-xl shadow-slate-200 flex items-center gap-6 min-w-[300px]">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
              <span className="text-xl font-bold text-emerald-400">৳</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-60">Total Earnings</p>
              <h3 className="text-3xl font-black italic">৳ {totalRevenue.toLocaleString()}</h3>
            </div>
          </div>
        </div>
  
        {/* --- SEARCH & FILTERS --- */}
        <div className="relative mb-8 group">
          <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <input
            type="text"
            placeholder="Search by Order ID or Customer Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-14 pr-6 py-4 bg-white border-none rounded-[2rem] shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-medium"
          />
        </div>
  
        {/* --- TRANSACTIONS TABLE --- */}
        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Transaction Date</th>
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Identity</th>
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Payment Type - Trx ID</th>
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Settled Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="4" className="py-20 text-center text-xs font-bold text-slate-300 uppercase tracking-[0.3em] animate-pulse">Syncing Ledger...</td>
                </tr>
              ) : filteredTransactions.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-8 py-6">
                    <div className="text-sm font-bold text-slate-900">{new Date(t.created_at).toLocaleDateString('en-GB')}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-1">#{t.id.slice(0, 8).toUpperCase()}</div>
                  </td>
                  <td className="px-8 py-6">
                    <div className="text-sm font-black text-slate-900 uppercase italic">
                      {t.app_users?.full_name || "Guest Checkout"}
                    </div>
                    <div className="text-[10px] font-bold text-slate-400 mt-0.5">{t.app_users?.phone || "No phone linked"}</div>
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-3">
                      <div className={`px-2 py-1 rounded text-[9px] font-black uppercase border ${
                        t.payment_method === 'bkash' ? 'bg-pink-50 text-pink-600 border-pink-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                      }`}>
                        {t.payment_method || 'N/A'}
                      </div> -
                      <span className="text-[10px] font-mono font-bold text-slate-400">{t.trx_id || "-------"}</span>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <div className="text-lg font-black italic text-emerald-600">
                      + ৳ {t.total_amount.toLocaleString()}
                    </div>
                  </td>
                </tr>
              ))}
              
              {!loading && filteredTransactions.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-20 text-center">
                    <div className="text-slate-300 font-bold uppercase text-[10px] tracking-widest">No matching transactions found</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
  
        {/* FOOTER STATS */}
        <div className="mt-8 flex justify-end">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-white px-6 py-2 rounded-full border border-slate-100 shadow-sm">
            Showing {filteredTransactions.length} successful payouts
          </p>
        </div>
      </div>
    </div>
  );
}